import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import { ADMIN_RECIPES } from "@/constants/testIds";

const TOKEN_KEY = "bayo_admin_token";

export default function AdminRecipesPage() {
  const { lang } = useLang();
  const t = useDict(lang);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState({ pending: [], rejected: [], total: 0 });
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async (tok) => {
    setError("");
    try {
      const res = await api.adminPendingRecipes(tok);
      setData(res);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, tok);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid token");
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    load(token);
  }, [token, load]);

  const submitToken = (e) => {
    e.preventDefault();
    load(token);
  };

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.adminApproveRecipe(token, id);
      await load(token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Approve failed");
    } finally { setBusyId(null); }
  };

  const startReject = (id) => {
    setRejectingId(id);
    setRejectReason("");
  };

  const confirmReject = async (id) => {
    setBusyId(id);
    try {
      await api.adminRejectRecipe(token, id, rejectReason);
      setRejectingId(null);
      setRejectReason("");
      await load(token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Reject failed");
    } finally { setBusyId(null); }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 pt-24 pb-32">
        <p className="eyebrow mb-4">№ Admin</p>
        <h1 className="font-serif-display text-5xl mb-10">{t.admin.recipesTitle}</h1>
        <form onSubmit={submitToken} className="space-y-6">
          <div>
            <label className="label-line">Admin token</label>
            <input
              data-testid={ADMIN_RECIPES.tokenInput}
              type="password"
              required
              className="input-line"
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-[color:var(--red)]">{error}</p>}
          <button data-testid={ADMIN_RECIPES.tokenSubmit} type="submit" className="btn-navy w-full justify-center">Continue</button>
        </form>
      </div>
    );
  }

  const renderCard = (r) => {
    const status = r.status;
    const isRejecting = rejectingId === r.recipe_id;
    return (
      <article
        key={r.recipe_id}
        data-testid={ADMIN_RECIPES.card(r.recipe_id)}
        className="border border-[color:rgba(0,16,48,0.15)] p-6 grid md:grid-cols-[200px_1fr] gap-6"
      >
        <img src={r.image} alt="" className="w-full h-48 md:h-full object-cover" />
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-[10px] tracking-[0.18em] uppercase font-medium px-2 py-1 border ${status === "pending" ? "bg-amber-100 text-amber-900 border-amber-300" : "bg-rose-100 text-rose-900 border-rose-300"}`}>
              {t.profile.status[status]}
            </span>
            <p className="eyebrow">{r.country} · {t.types[r.type]} · {r.time_minutes} min</p>
          </div>
          <h2 className="font-serif-display text-3xl">{r.title.fr}</h2>
          <p className="text-sm text-[color:var(--ink-2)]">{t.admin.author}: {r.author_name || "Anonyme"}</p>
          {r.summary?.fr && <p className="text-sm text-[color:var(--ink-2)] italic">{r.summary.fr}</p>}
          <details className="text-sm text-[color:var(--ink-2)]">
            <summary className="cursor-pointer underline">{t.admin.ingredients} ({(r.ingredients?.fr || []).length})</summary>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              {(r.ingredients?.fr || []).map((i, idx) => <li key={`${r.recipe_id}-ing-${idx}-${i.slice(0,12)}`}>{i}</li>)}
            </ul>
          </details>
          <details className="text-sm text-[color:var(--ink-2)]">
            <summary className="cursor-pointer underline">{t.admin.steps} ({(r.steps?.fr || []).length})</summary>
            <ol className="list-decimal pl-5 mt-2 space-y-1">
              {(r.steps?.fr || []).map((s, idx) => <li key={`${r.recipe_id}-step-${idx}-${s.slice(0,12)}`}>{s}</li>)}
            </ol>
          </details>
          {status === "rejected" && r.rejection_reason && (
            <p className="text-xs italic text-[color:var(--ink-3)]">{t.profile.rejectionReason}: {r.rejection_reason}</p>
          )}
          {status === "pending" && (
            <div className="flex gap-3 flex-wrap mt-2">
              <button
                data-testid={ADMIN_RECIPES.approve(r.recipe_id)}
                disabled={busyId === r.recipe_id}
                onClick={() => approve(r.recipe_id)}
                className="btn-navy"
              >
                {busyId === r.recipe_id ? "..." : t.admin.approve}
              </button>
              {!isRejecting ? (
                <button
                  data-testid={ADMIN_RECIPES.reject(r.recipe_id)}
                  onClick={() => startReject(r.recipe_id)}
                  className="btn-outline"
                >
                  {t.admin.reject}
                </button>
              ) : (
                <div className="flex gap-2 flex-1 min-w-[220px]">
                  <input
                    data-testid={ADMIN_RECIPES.rejectReason(r.recipe_id)}
                    placeholder={t.admin.rejectReason}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="input-line flex-1"
                  />
                  <button
                    data-testid={ADMIN_RECIPES.rejectConfirm(r.recipe_id)}
                    disabled={busyId === r.recipe_id}
                    onClick={() => confirmReject(r.recipe_id)}
                    className="btn-navy"
                  >
                    {busyId === r.recipe_id ? "..." : "OK"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </article>
    );
  };

  return (
    <div data-testid={ADMIN_RECIPES.page} className="max-w-[1200px] mx-auto px-6 lg:px-12 pt-16 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
        <div>
          <p className="eyebrow mb-2">№ Admin</p>
          <h1 className="font-serif-display text-5xl">{t.admin.recipesTitle}</h1>
          <p className="text-[color:var(--ink-2)] mt-2">{t.admin.recipesSub}</p>
        </div>
        <Link to="/admin/leads" data-testid={ADMIN_RECIPES.switchToLeads} className="btn-outline">{t.admin.switchToLeads}</Link>
      </div>

      {error && <p className="text-sm text-[color:var(--red)] mb-6">{error}</p>}

      <section className="mb-16">
        <h2 className="font-serif-display text-3xl mb-6">{t.admin.pending} ({data.pending.length})</h2>
        {data.pending.length === 0 ? (
          <p data-testid={ADMIN_RECIPES.empty} className="text-[color:var(--ink-3)] italic font-serif-display text-2xl">{t.admin.noPending}</p>
        ) : (
          <div className="grid gap-6">{data.pending.map(renderCard)}</div>
        )}
      </section>

      {data.rejected.length > 0 && (
        <section>
          <h2 className="font-serif-display text-3xl mb-6">{t.admin.rejected} ({data.rejected.length})</h2>
          <div className="grid gap-6">{data.rejected.map(renderCard)}</div>
        </section>
      )}
    </div>
  );
}
